/**
 * Google Publisher Tag (GPT) utilities for Google Ad Manager integration
 * Based on DraftKings Tagging Architecture requirements
 */

import type { AdRefreshConfig, AdSlotConfig, PageTargeting, ResponsiveSizeMapping } from './types';

// Extend Window interface for GPT
declare global {
  interface Window {
    googletag: any;
  }
}

// URL of the Google Publisher Tag library
const GPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

/**
 * Ensure the Google Publisher Tag (gpt.js) library is present in the document.
 *
 * Apps that already include gpt.js in their HTML <head> (e.g. the existing
 * frontend/squares/daily-reveal apps) are unaffected: the presence checks below
 * short-circuit. For host applications that do NOT load gpt.js themselves (such
 * as a client's external React system), this injects the script so the tracking
 * ad components "just work" with zero HTML setup.
 *
 * Safe to call multiple times — it only injects once.
 */
export const ensureGptLoaded = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Already loaded/ready — nothing to do
  if (window.googletag && window.googletag.apiReady) {
    return;
  }

  // Seed the command queue so callers can push commands before the script loads
  window.googletag = window.googletag || { cmd: [] };

  // A gpt.js tag is already on the page (possibly still loading) — don't add another
  const existing = document.querySelector(`script[src*="gpt.js"]`);
  if (existing) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.type = 'text/javascript';
  script.src = GPT_SRC;
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
  console.log('[GPT] Injected gpt.js library');
};

export const initializeGPT = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if GPT is already loaded and ready
    if (window.googletag && window.googletag.apiReady) {
      console.log('[GPT] Already initialized');
      resolve();
      return;
    }

    // Ensure googletag object exists (should be created by script in HEAD)
    window.googletag = window.googletag || { cmd: [] };

    // Wait for the script loaded from HEAD to become ready
    // The script in HEAD creates the googletag object and loads gpt.js
    let attempts = 0;
    const maxAttempts = 200; // 10 seconds max (200 * 50ms)

    const checkGPT = () => {
      attempts++;

      // Check if GPT API is ready
      if (window.googletag && window.googletag.apiReady) {
        console.log('[GPT] Script loaded from HEAD, initialized');
        resolve();
        return;
      }

      // Check if we've exceeded max attempts
      if (attempts >= maxAttempts) {
        console.error('[GPT] Timeout waiting for script to load from HEAD');
        reject(new Error('Timeout waiting for GPT script to load'));
        return;
      }

      // Check again after a short delay
      setTimeout(checkGPT, 50);
    };

    // Start checking after a short delay to allow script to start loading
    setTimeout(checkGPT, 100);
  });
};

/**
 * Set page-level targeting parameters
 * Supports both DraftKings standard keys (s1, s2, s3, pos, pid, contestid)
 * and legacy keys for backward compatibility
 */
export const setPageTargeting = (targeting: PageTargeting): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }

  window.googletag.cmd.push(() => {
    const pubads = window.googletag.pubads();

    // Dynamically set all targeting parameters
    Object.entries(targeting).forEach(([key, value]) => {
      if (value) {
        pubads.setTargeting(key, value);
      }
    });

    console.log('[GPT] Page-level targeting set:', targeting);
  });
};

/**
 * Build responsive size mapping for GPT
 * Converts our ResponsiveSizeMapping format to googletag.SizeMapping format
 */
export const buildSizeMapping = (sizeMappings: ResponsiveSizeMapping[]): any => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return null;
  }

  const mapping = window.googletag.sizeMapping();

  // Sort mappings by viewport width (largest first) for proper fallback
  const sortedMappings = [...sizeMappings].sort((a, b) => b.viewport[0] - a.viewport[0]);

  sortedMappings.forEach(({ viewport, sizes }) => {
    mapping.addSize(viewport, sizes);
  });

  return mapping.build();
};

/**
 * Define an ad slot with optional responsive size mappings
 * Based on DraftKings GPT integration pattern:
 * 1. Define slot with sizes
 * 2. Apply size mapping if provided
 * 3. Add service
 * 4. Apply slot-level targeting
 *
 * Note: Page-level targeting should be set separately using setPageTargeting()
 * before or after defining slots (both work, but setting before is recommended)
 */
export const defineAdSlot = (config: AdSlotConfig): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }

  window.googletag.cmd.push(() => {
    const slot = window.googletag.defineSlot(config.adUnitPath, config.sizes, config.divId);

    if (config.sizeMappings && config.sizeMappings.length > 0) {
      const mapping = buildSizeMapping(config.sizeMappings);
      if (mapping) {
        slot.defineSizeMapping(mapping);
        console.log('[GPT] Applied responsive size mapping to:', config.divId);
      }
    }

    slot.addService(window.googletag.pubads());

    if (config.targeting) {
      // Skip empty values so a malformed config can never push a blank targeting
      // key onto the slot.
      const applied: Record<string, string> = {};
      Object.entries(config.targeting).forEach(([key, value]) => {
        if (key && value) {
          slot.setTargeting(key, value);
          applied[key] = value;
        }
      });
      console.log('[GPT] Applied slot-level targeting to:', config.divId, applied);
    }

    console.log('[GPT] Defined ad slot:', config.divId, 'with path:', config.adUnitPath);
  });
};

export const enableAndDisplayAds = (): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }

  window.googletag.cmd.push(() => {
    window.googletag.pubads().enableSingleRequest();
    window.googletag.pubads().collapseEmptyDivs();
    window.googletag.enableServices();
    console.log('[GPT] Services enabled');
  });
};

/**
 * Display a specific ad slot
 * This triggers the actual rendering of the ad into the div
 */
export const displayAdSlot = (divId: string): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }
  window.googletag.cmd.push(() => {
    window.googletag.display(divId);
    console.log('[GPT] Displaying ad:', divId);
  });
};

/**
 * Destroy an ad slot (cleanup)
 */
export const destroyAdSlot = (divId: string): void => {
  if (!window.googletag) {
    return;
  }

  window.googletag.cmd.push(() => {
    const slots = window.googletag.pubads().getSlots();
    const slot = slots.find((s: any) => s.getSlotElementId() === divId);
    if (slot) {
      window.googletag.destroySlots([slot]);
      console.log('[GPT] Destroyed ad slot:', divId);
    }
  });
};

/**
 * Refresh specific ad slots
 * Used for manual refresh or as part of automatic refresh cycle
 */
export const refreshAdSlots = (divIds: string[]): void => {
  if (!window.googletag) {
    return;
  }

  window.googletag.cmd.push(() => {
    const slots = window.googletag.pubads().getSlots();
    const slotsToRefresh = slots.filter((s: any) => divIds.includes(s.getSlotElementId()));

    if (slotsToRefresh.length > 0) {
      window.googletag.pubads().refresh(slotsToRefresh);
      console.log('[GPT] Refreshed ad slots:', divIds);
    }
  });
};

/**
 * Start automatic ad refresh for specified slots
 * Returns a cleanup function to stop the refresh cycle
 *
 * @param config - Refresh configuration with divIds, interval, and optional max refreshes
 * @returns Cleanup function to stop refresh cycle
 */
export const startAdRefresh = (config: AdRefreshConfig): (() => void) => {
  let refreshCount = 0;
  const { divIds, intervalSeconds, maxRefreshes = 0 } = config;

  console.log(
    `[GPT] Starting ad refresh for ${divIds.length} slot(s) every ${intervalSeconds}s` +
      (maxRefreshes > 0 ? ` (max ${maxRefreshes} refreshes)` : '')
  );

  const intervalId = setInterval(() => {
    // Check if we've reached max refreshes
    if (maxRefreshes > 0 && refreshCount >= maxRefreshes) {
      console.log(`[GPT] Reached max refresh count (${maxRefreshes}), stopping refresh`);
      clearInterval(intervalId);
      return;
    }

    refreshAdSlots(divIds);
    refreshCount++;
  }, intervalSeconds * 1000);

  // Return cleanup function
  return () => {
    console.log(
      `[GPT] Stopping ad refresh for ${divIds.length} slot(s) (refreshed ${refreshCount} times)`
    );
    clearInterval(intervalId);
  };
};

/**
 * Subscribe to GPT's `slotRenderEnded` event for a single slot div.
 *
 * The callback receives `true` when the slot rendered with no creative (GAM had
 * nothing to serve), which is the only reliable signal that reserved ad space
 * should be collapsed — `collapseEmptyDivs()` only collapses GPT's own div, not
 * any wrapper the app reserved space on.
 *
 * Returns an unsubscribe function; safe to call before gpt.js has loaded.
 */
export const onAdSlotRenderEnded = (
  divId: string,
  callback: (isEmpty: boolean) => void
): (() => void) => {
  if (typeof window === 'undefined' || !window.googletag) {
    return () => {};
  }

  let unsubscribed = false;
  let listener: ((event: any) => void) | null = null;

  window.googletag.cmd.push(() => {
    if (unsubscribed) return;
    listener = (event: any) => {
      if (event?.slot?.getSlotElementId?.() !== divId) return;
      callback(Boolean(event.isEmpty));
    };
    window.googletag.pubads().addEventListener('slotRenderEnded', listener);
  });

  return () => {
    unsubscribed = true;
    if (!listener || !window.googletag) return;
    const registered = listener;
    listener = null;
    window.googletag.cmd.push(() => {
      window.googletag.pubads().removeEventListener('slotRenderEnded', registered);
    });
  };
};
